import { createSlice } from '@reduxjs/toolkit';

import { overlayButtonType, layerSourceType } from '@/constants';
import type {
  LayerAsset,
  OverlayData,
  Layer,
  LayerAttributesContents,
} from '@/types';
import { hasOwn } from '@/utils/hasOwn';

const initialState: OverlayData = {
  data: {},
  tags: [],
  enableLayers: null,
  defaultEnableLayers: null,
  targetLayerStatus: {
    targetLayerId: null,
    status: null,
  },
};

export const overlaySlice = createSlice({
  name: 'overlays',
  initialState,
  reducers: {
    clearLayerInfo: (state) => {
      state.data = {};
      state.enableLayers = null;
      state.defaultEnableLayers = null;
    },
    setOverlayInfo: (state, { payload }) => {
      const assetType = payload.assetType;
      const classification = assetType.attributes.classification;
      const buttonType =
        assetType.attributes.buttonType ?? overlayButtonType.LIST;
      if (classification === 'overlay') {
        const assetTypeId = assetType.id;
        const name = assetType.name;
        const mapType = assetType.attributes.mapType;
        const assetAttributesSchema = assetType.assetAttributesSchema;
        const layers: Layer[] = [];
        const overlay = {
          name,
          buttonType,
          mapType,
          assetAttributesSchema,
          layers,
          assetType,
        };

        state.data[assetTypeId] = overlay;
      }
    },
    setLayerInfo: (state, { payload }) => {
      payload.forEach((asset: LayerAsset) => {
        const attributes = asset.attributes;
        const assetProperties = asset.assetProperties ?? [];
        const getLayerIdFromLayerSource = () => {
          if (asset.attributes.layerSource === layerSourceType.CUCON) {
            return asset.id;
          } else {
            return asset.attributes.id;
          }
        };

        // attributesからattributesの外で定義しているプロパティを除外する;
        const {
          color,
          display,
          layerSource,
          tags,
          kana,
          index,
          hasDetailPage,
          period,
          ...formattedAttributes
        } = attributes;
        const name = asset.name;
        const location = asset.location;
        const id = getLayerIdFromLayerSource();
        const description = asset.description;
        const updatedAt = asset.updatedAt;
        const assetTypeId = asset.assetTypeId ?? '';

        if ('contents' in formattedAttributes) {
          let contents = {} as LayerAttributesContents;
          formattedAttributes.contents?.forEach((content, index) => {
            contents = {
              ...contents,
              [`d${index}`]: {
                contentType:
                  state.data[asset.assetTypeId].assetAttributesSchema[index]
                    .contentType,
                label:
                  state.data[asset.assetTypeId].assetAttributesSchema[index]
                    .label,
                order: index,
                value: content.value,
                url: content.url,
                isShowLabel:
                  state.data[asset.assetTypeId].assetAttributesSchema[index]
                    .isShowLabel,
                movieHtml: content.movieHtml,
              },
            };
          });
          state.data[asset.assetTypeId].layers.push({
            name,
            location,
            layerSource,
            display,
            id,
            assetTypeId,
            color,
            tags,
            kana,
            index,
            hasDetailPage,
            description,
            period,
            attributes: contents ?? {},
            assetProperties,
            ...(attributes.isEnabledBadge != null && {
              isEnabledBadge: attributes.isEnabledBadge,
            }),
            updatedAt,
          });
        } else {
          // @COMPATIBILITY 豊中形式のデータに互換させるため、attributes直下にあるコンテンツの場合は以下の処理を行う
          state.data[asset.assetTypeId].layers.push({
            name,
            location,
            layerSource,
            display,
            id,
            color,
            tags,
            kana,
            index,
            hasDetailPage,
            description,
            attributes: formattedAttributes,
            assetProperties,
            updatedAt,
          });
        }
      });
    },
    setLayerTags: (state, { payload }) => {
      state.tags = payload;
    },
    setEnableLayers: (state, { payload }) => {
      const enableLayers = (state.enableLayers = payload);
      const overlays = state.data;
      if (enableLayers !== null) {
        Object.keys(overlays).forEach((key) => {
          const layers = overlays[key].layers;
          const enableLayer = enableLayers[key] as { layerNames: string[] };
          if (enableLayer !== undefined) {
            layers.forEach((layer) => {
              layer.display = false;
              if (
                layer.layerSource === layerSourceType.MAPBOX ||
                layer.layerSource === layerSourceType.DISAPORTALDATA
              ) {
                if (enableLayer.layerNames.includes(layer.name)) {
                  layer.display = true;
                }
              }
              // NOTE: カスタムレイヤーの場合は同名のレイヤーが存在する可能性があるためidでハンドリング
              if (layer.layerSource === layerSourceType.CUCON) {
                if (
                  typeof layer.id === 'string' &&
                  enableLayer.layerNames.includes(layer.id)
                ) {
                  layer.display = true;
                }
              }
            });
          }
        });
      }
    },
    setDefaultEnableLayers: (state, { payload }) => {
      state.defaultEnableLayers = payload;
    },
    changeDisplayStatus: (state, { payload }) => {
      const id = payload.id;
      const name = payload.name;
      const overlays = state.data;
      const flag = payload.flag;
      const enableLayers = state.enableLayers;
      //クリックしたmapTypeがoverlayを持っていなかったら、overlayを削除する
      if (payload.hasOverlay === false) {
        Object.keys(overlays).forEach((key) => {
          const layers = overlays[key].layers;
          layers.forEach((layer) => {
            if (
              layer.layerSource === layerSourceType.MAPBOX ||
              layer.layerSource === layerSourceType.DISAPORTALDATA
            ) {
              if (layer.name === name) {
                layer.display = false;
              }
            }
            // NOTE: カスタムレイヤーの場合は同名のレイヤーが存在する可能性があるためidでハンドリング
            if (layer.layerSource === layerSourceType.CUCON) {
              if (layer.id === id) {
                layer.display = false;
              }
            }
          });
        });
        state.enableLayers = null;
        state.defaultEnableLayers = null;
      } else {
        Object.keys(overlays).forEach((key) => {
          const layers = overlays[key].layers;
          layers.forEach((layer) => {
            let layerId = '';
            if (
              layer.layerSource === layerSourceType.MAPBOX ||
              layer.layerSource === layerSourceType.DISAPORTALDATA
            ) {
              if (layer.name === name) {
                layer.display = !layer.display;
                layerId = name;
              }
            }
            // NOTE: カスタムレイヤーの場合は同名のレイヤーが存在する可能性があるためidでハンドリング
            if (layer.layerSource === layerSourceType.CUCON) {
              if (layer.id === id) {
                if (flag != undefined) {
                  layer.display = flag;
                } else {
                  layer.display = !layer.display;
                }
                layerId = id;
              }
            }
            if (layerId !== '' && enableLayers !== null) {
              if (layer.display) {
                if (!hasOwn(enableLayers, key)) {
                  enableLayers[key] = { layerNames: [] };
                }
                if (enableLayers[key].layerNames.indexOf(layerId) === -1) {
                  enableLayers[key].layerNames.push(layerId);
                }
              } else {
                if (hasOwn(enableLayers, key)) {
                  const index = enableLayers[key].layerNames.indexOf(layerId);
                  if (index !== -1) {
                    enableLayers[key].layerNames.splice(index, 1);
                  }
                }
              }
            }
          });
        });
      }
    },
    changeDisplayTypeStatus: (state, { payload }) => {
      const id = payload.id;
      const flag = payload.flag as boolean;
      const overlays = state.data;
      const enableLayers = state.enableLayers;
      const layers = overlays[id].layers;
      if (enableLayers !== null) {
        enableLayers[id] = { layerNames: [] };
        layers.forEach((layer) => {
          let layerId = '';
          if (
            layer.layerSource === layerSourceType.MAPBOX ||
            layer.layerSource === layerSourceType.DISAPORTALDATA
          ) {
            layer.display = flag;
            layerId = layer.name;
          }
          // NOTE: カスタムレイヤーの場合は同名のレイヤーが存在する可能性があるためidでハンドリング
          if (layer.layerSource === layerSourceType.CUCON) {
            layerId = layer.id as string;
          }
          if (flag) {
            enableLayers[id].layerNames.push(layerId);
          }
        });
      }
    },
    setTargetLayerStatus: (state, action) => {
      state.targetLayerStatus = {
        targetLayerId: action.payload.targetLayerId,
        status: action.payload.status,
      };
    },
  },
});

export const {
  reducer: overlayReducer,
  actions: {
    clearLayerInfo,
    setOverlayInfo,
    setLayerInfo,
    setLayerTags,
    setEnableLayers,
    setDefaultEnableLayers,
    changeDisplayStatus,
    changeDisplayTypeStatus,
    setTargetLayerStatus,
  },
} = overlaySlice;
